import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { fmtMoneyShort, fmtMoney } from '../engine.js';

const TOOLTIP_CLS = 'bg-[rgba(18,21,28,0.95)] border border-border-strong rounded-sm px-[0.7rem] py-2 min-w-[180px] text-[0.8rem] text-text';

export default function VarianceWaterfallChart({ budget, actual, forecast, label = '12mo net FCF', height = 280 }) {
  const actualDelta   = actual   - budget;
  const forecastDelta = forecast - actual;

  const data = [
    { name: 'Budgets',          base: 0,                  bar: budget,         total: budget,   tone: 'anchor' },
    { name: 'Actual Δ',         base: Math.min(budget, actual),  bar: Math.abs(actualDelta),    total: actual,   tone: actualDelta >= 0 ? 'good' : 'bad', delta: actualDelta },
    { name: 'Forecasts Δ',      base: Math.min(actual, forecast), bar: Math.abs(forecastDelta), total: forecast, tone: forecastDelta >= 0 ? 'good' : 'bad', delta: forecastDelta },
    { name: 'Forecasts',        base: 0,                  bar: forecast,       total: forecast, tone: 'anchor' },
  ];

  const colorFor = (tone) => {
    if (tone === 'good')   return '#4ade80';
    if (tone === 'bad')    return '#f87171';
    return '#60a5fa';
  };

  const toneColorClass = (tone) =>
    tone === 'good' ? 'text-accent-green'
    : tone === 'bad' ? 'text-accent-red'
    : 'text-accent-blue';

  const fmtY = (v) => fmtMoneyShort(v);

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className={TOOLTIP_CLS}>
        <strong className="block mb-1 text-accent-gold">{d.name}</strong>
        <div>Total: {fmtMoney(d.total, { decimals: 2 })}</div>
        {d.delta != null && (
          <div className={`tabular-nums text-[0.8rem] ${toneColorClass(d.tone)}`}>
            Δ {d.delta >= 0 ? '+' : ''}{fmtMoney(d.delta, { decimals: 2 })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-[0.4rem]">
      <div className="text-[0.78rem] text-text-muted tracking-[0.04em] uppercase">{label} — variance walk</div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 16, right: 20, bottom: 0, left: 8 }}>
          <CartesianGrid stroke="var(--grid)" strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} stroke="var(--border)" />
          <YAxis tickFormatter={fmtY} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} stroke="var(--border)" />
          <Tooltip content={customTooltip} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <ReferenceLine y={0} stroke="var(--border-strong)" />
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="bar"  stackId="w" isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell key={i} fill={colorFor(d.tone)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
